import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  House,
  HandsPraying,
  PlayCircle,
  BookOpen,
  UsersThree,
  Heart,
  User,
  GearSix,
  ShieldCheck,
} from "@phosphor-icons/react";

const mainTabs = [
  { to: "/home", icon: House, label: "Home" },
  { to: "/guided-prayer", icon: HandsPraying, label: "Pray" },
  { to: "/learn", icon: BookOpen, label: "Learn" },
  { to: "/community", icon: UsersThree, label: "Community" },
  { to: "/give", icon: Heart, label: "Give" },
];

function VoiceBrand({ className = "" }) {
  return (
    <span className={`font-heading tracking-[0.2em] text-[#2D1B0E] ${className}`}>
      VO<span className="text-[#C4A265]">&#10013;</span>CE
    </span>
  );
}

export default function AppLayout({ children, headerless = false }) {
  const { user } = useAuth();
  const location = useLocation();

  const pageTitle = (() => {
    const p = location.pathname;
    if (p.startsWith("/home")) return "Home";
    if (p.startsWith("/guided-prayer/personal")) return "Personal Prayer";
    if (p.startsWith("/guided-prayer")) return "Guided Prayer";
    if (p.startsWith("/watch")) return "Watch";
    if (p.startsWith("/learn")) return "Learn";
    if (p.startsWith("/community")) return "Community";
    if (p.startsWith("/give")) return "Give";
    if (p.startsWith("/connect")) return "Get Guidance";
    if (p.startsWith("/worship")) return "Worship";
    if (p.startsWith("/profile")) return "Profile";
    if (p.startsWith("/journal")) return "Journal";
    if (p.startsWith("/admin")) return "Admin";
    return "";
  })();

  return (
    <div className="min-h-screen" data-testid="app-layout">
      {/* Desktop Top Nav */}
      {!headerless && (
        <header className="hidden md:flex items-center justify-between px-8 py-3 glass-nav fixed top-0 left-0 right-0 z-50" data-testid="desktop-nav">
          <div className="flex items-center gap-10">
            <Link to="/home">
              <VoiceBrand className="text-base" />
            </Link>
            <nav className="flex items-center gap-1">
              {mainTabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  data-testid={`desktop-nav-${tab.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-body transition-all duration-300 ${
                      isActive
                        ? "bg-[#C4A265]/10 text-[#5C4033] font-medium"
                        : "text-[#8B7355] hover:text-[#5C4033] hover:bg-[#EDE6DC]/50"
                    }`
                  }
                >
                  <tab.icon size={18} weight="duotone" />
                  {tab.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <NavLink to="/admin" className={({ isActive }) =>
                `p-2 rounded-full transition-all ${isActive ? "bg-[#C4A265]/10 text-[#5C4033]" : "text-[#8B7355] hover:text-[#5C4033]"}`
              }>
                <ShieldCheck size={20} weight="duotone" />
              </NavLink>
            )}
            <Link to="/profile" data-testid="desktop-profile-btn" className="w-9 h-9 rounded-full bg-[#EDE6DC] flex items-center justify-center text-[#8B7355] hover:bg-[#E0D5C5] transition-all">
              <User size={18} weight="duotone" />
            </Link>
          </div>
        </header>
      )}

      {/* Mobile Header */}
      {!headerless && (
        <header className="md:hidden flex items-center justify-between px-5 py-3" data-testid="mobile-header">
          <VoiceBrand className="text-sm" />
          <span className="font-heading text-base text-[#2D1B0E]">{pageTitle}</span>
          <Link to="/profile" data-testid="mobile-profile-btn" className="w-8 h-8 rounded-full bg-[#EDE6DC] flex items-center justify-center text-[#8B7355]">
            <User size={16} weight="duotone" />
          </Link>
        </header>
      )}

      {/* Main Content */}
      <main className={`${!headerless ? "md:pt-14" : ""} pb-20 md:pb-4`}>
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 glass-nav z-50 safe-area-bottom" data-testid="mobile-nav">
        <div className="flex justify-around py-2 px-2">
          {mainTabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              data-testid={`mobile-nav-${tab.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-xl transition-all ${
                  isActive ? "text-[#5C4033]" : "text-[#B8A088]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <tab.icon size={22} weight={isActive ? "fill" : "duotone"} />
                  <span className="text-[10px] font-body font-medium">{tab.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export { VoiceBrand };
